import type { HolePenalty, ShotLanding, ShotPenalty } from '@golf/common';
import {
    SHOT_LANDING_LABELS, SHOT_PENALTY_LABELS, defaultPenalty,
    holePenaltyStrokes, holePenaltyTotal, penaltyStrokes,
} from '@golf/common';
import {
    Bird, Check, ChevronRight, Flag, Frown, GraduationCap, Hand, Lightbulb, LogOut, PartyPopper, Skull, ThumbsUp, Trash2, Trophy,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import { BasculeNet } from '../../components/BasculeNet.tsx';
import { useAuth } from '../../lib/auth.tsx';
import { rendusDuJoueur, useScoreNet } from '../../lib/scoreNet.ts';
import type { ClubEnJeu, Suggestion } from '../../lib/clubChoice.ts';
import { clubPour, listeDesClubs } from '../../lib/clubChoice.ts';
import type { RoundState } from '../../lib/rounds.ts';
import {
    deleteRound, deleteShot, ecartAuPar, finishRound, garderTour, getBag, getCurrentRound, saveHole,
    saveShot,
} from '../../lib/rounds.ts';
import {
    ChoixDuClub, MarqueMemoire, MarqueTuile, PourquoiCeClub, RaisonLigne,
} from '../../components/ChoixDuClub.tsx';
import { Etape } from '../../components/Etape.tsx';
import { SaisieDistance } from './SaisieDistance.tsx';
import { BilanCoup } from './BilanCoup.tsx';
import { ChoixClubEcran } from './ChoixClubEcran.tsx';
import { BilanTrou } from './BilanTrou.tsx';
import { Carte, allure } from './Carte.tsx';
import { CartesJoueurs } from './CartesJoueurs.tsx';
import { BoutonPenalite, FeuillePenalite } from './PavePenalite.tsx';
import { PileAvatars } from './PileAvatars.tsx';

/* L'écran de parcours, bâti sur celui du practice.
 *
 * ── Pourquoi le même gabarit ───────────────────────────────────────────────
 *
 * Quatre étages, dans le même ordre qu'à l'entraînement : le titre et l'arrêt,
 * un bandeau de quatre valeurs, la carte du club, le pavé d'issues qui prend
 * tout ce qui reste. Un joueur qui a fait dix séances au practice ne réapprend
 * rien sur le premier départ — ce qui change d'un écran à l'autre, ce sont les
 * VALEURS, pas les gestes.
 *
 * Le bandeau porte ce qui est propre au parcours : trou, par, distance, index.
 * Sous le bouton de club viennent les coups déjà joués, un par pastille, comme
 * les balles d'une série.
 *
 * ── Le pavé remplace le bouton « + Coup » ──────────────────────────────────
 *
 * Un appui sur le pavé ENREGISTRE le coup : il compte pour la carte et note ce
 * que la balle a fait. C'est le même geste qu'avant — un appui — mais il
 * rapporte ce qui alimente les statistiques au lieu d'un simple incrément.
 *
 * Un putt n'a pas de bouton à lui : c'est le CLUB qui le dit. Putter choisi,
 * le coup est un putt. C'est le seul coup dont l'outil ne fait aucun doute, et
 * doubler les boutons pour l'annoncer serait redondant.
 *
 * ── Le club se propose au départ, se choisit ensuite ───────────────────────
 *
 * Sur le départ la distance est écrite dans le carnet. Dès le deuxième coup
 * l'application ne sait plus rien de la distance restante, faute de GPS : elle
 * se tait plutôt que de deviner. Une suggestion fausse coûte un coup.
 */

/** Ce que le parcours a à dire du club qu'il propose : la distance qui tombe
 *  le plus près, et ce que les séances disent de ce club. Le cadre et le titre
 *  viennent du module partagé ; ces phrases-ci sont les seules que le parcours
 *  seul peut écrire. */
function Pourquoi({ suggestion, cibleM }: {
    suggestion: Suggestion; cibleM: number | null;
}) {
    const c = suggestion.club;
    const taux = c.shots > 0 ? Math.round((c.center * 100) / c.shots) : null;
    return (
        <PourquoiCeClub label={c.label}>
            {c.referenceDistanceM != null && cibleM != null && (
                <RaisonLigne>
                    <span className="tabular-nums">{c.referenceDistanceM} m</span> contre{' '}
                    <span className="tabular-nums">{cibleM} m</span> à couvrir — c'est le club de
                    votre sac dont la distance tombe le plus près.
                </RaisonLigne>
            )}
            {/* Ce que vos entraînements disent de ce club, en une phrase. Deux
                points séparés — le taux, puis la distance travaillée —
                obligeaient à recomposer soi-même le lien entre les deux, qui est
                justement l'information. */}
            <RaisonLigne>
                {c.practiceDistanceM != null && taux != null ? (
                    <>
                        À l'entraînement, sur une moyenne de{' '}
                        <span className="tabular-nums">{c.practiceDistanceM} m</span>, vous le
                        réussissez <span className="tabular-nums">{taux} %</span> du temps.
                    </>
                ) : taux != null ? (
                    <>
                        À l'entraînement, vous le réussissez{' '}
                        <span className="tabular-nums">{taux} %</span> du temps.
                    </>
                ) : (
                    <>Vous ne l'avez pas encore travaillé au practice : seul l'écart de distance a décidé.</>
                )}
            </RaisonLigne>
        </PourquoiCeClub>
    );
}

/* L'icône du score, quand le trou se ferme.
 *
 * Le golf nomme ses scores avec des oiseaux ; l'écran les montre. L'oiseau du
 * birdie est littéral, et le crâne du double dit en une image ce que « +2 »
 * dit en chiffres — c'est le moment de la partie où l'on a le droit de sourire
 * ou de grimacer, pas celui où il faut lire.
 *
 * Une flèche de tendance était juste mais froide : elle mesurait là où le
 * joueur, lui, raconte. */
function IconeScore({ coups, par, size }: { coups: number; par: number; size: number }) {
    const e = coups - par;
    if (coups === 1) return <PartyPopper size={size} aria-hidden="true" />;
    if (e <= -2) return <Trophy size={size} aria-hidden="true" />;
    if (e === -1) return <Bird size={size} aria-hidden="true" />;
    if (e === 0) return <ThumbsUp size={size} aria-hidden="true" />;
    if (e === 1) return <Frown size={size} aria-hidden="true" />;
    return <Skull size={size} aria-hidden="true" />;
}

/** Une valeur du bandeau : le mot d'abord, la valeur dessous.
 *
 *  L'ordre inverse — chiffre puis libelle — obligeait a lire de bas en haut
 *  pour savoir ce que le chiffre designait. Le mot annonce, la valeur repond ;
 *  c'est le sens de lecture, et il se retient a la premiere lecture. */
/* Un fait du trou : par, distance, index.
 *
 * Ces trois valeurs étaient blanches sur le panneau vert. Le panneau porte
 * maintenant le ruban du tour, et elles ont descendu sur le papier de la
 * carte — d'où l'encre à la place du blanc. Elles gardent la police de
 * tableau d'affichage : ce sont des chiffres qu'on lit à bout de bras, au
 * moment de choisir un club.
 *
 * L'étiquette au-dessus et non à côté : trois colonnes de deux étages tiennent
 * dans la largeur d'un téléphone, trois paires « mot valeur » non. */
function Fait({ v, mot }: { v: string; mot: string }) {
    return (
        <span className="min-w-0">
            {/* --tuile-encre et non sa version pâle : à 10 px, en capitales,
                l'encre pâle ne tenait que 3,4:1 sur le papier de la carte —
                sous les 4,5:1 qu'un texte de cette taille exige. Celle-ci en
                donne 6,8, et c'est déjà la couleur des titres d'étape : même
                registre, même encre. */}
            <span className="block text-[10px] font-bold uppercase tracking-wide"
                style={{ color: 'var(--tuile-encre)' }}>
                {mot}
            </span>
            <span className="font-score block truncate text-lg leading-tight">{v}</span>
        </span>
    );
}

/** Toutes les cinq secondes tant qu'on attend une réponse. Court, parce que
 *  l'on est arrêté au départ avec le téléphone en main et que dix secondes de
 *  décalage suffisent à faire relancer l'application. */
const ATTENTE_MS = 5000;

export function RoundPlay() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const moi = user?.id ?? null;
    const [etat, setEtat] = useState<RoundState | null>(null);

    /* La carte est recopiee sur le telephone a chaque changement.
     *
     * C'est elle qu'on rouvrira au trou suivant, dans un creux sans reseau :
     * l'ecran tient la verite entre deux synchronisations, et sans cette copie
     * elle disparaitrait avec l'onglet. */
    // Le ruban SUIT la préférence ; elle se change depuis « Les cartes »,
    // seul endroit du tour où la carte est montrée en entier.
    const [scoreNet, setScoreNet] = useScoreNet();
    /* Mes coups rendus sur ce parcours, depuis MON départ.
     *
     * Recalculés à chaque changement de carte plutôt que demandés au serveur :
     * la formule est la même pour tout le monde, elle tient en trois lignes, et
     * un tour joué sans réseau doit pouvoir afficher son net. */
    const mesRendus = useMemo(() => {
        const ligne = etat?.players.find((j) => j.userId === moi);
        if (!ligne || !etat) return undefined;
        return rendusDuJoueur(ligne, etat.holes, etat.round?.maxHandicapStrokes);
    }, [etat, moi]);

    /** Le net est-il lisible ? Il demande un index ET l'ordre de difficulté des
     *  trous ; sans l'un des deux, la bascule n'aurait aucun effet, et mieux
     *  vaut ne pas la montrer que la montrer inerte. */
    const netLisible = mesRendus != null && mesRendus.total != null && mesRendus.indexConnus;

    useEffect(() => {
        // Rien tant qu'il n'y a rien : au montage, l'etat est nul le temps du
        // chargement, et l'ecrire ECRASERAIT la copie que ce chargement est
        // justement en train de lire. C'est ainsi qu'une carte gardee la
        // veille disparaissait a la premiere ouverture sans reseau.
        if (etat) void garderTour(etat);
    }, [etat]);
    const [clubs, setClubs] = useState<ClubEnJeu[]>([]);
    const [trou, setTrou] = useState(1);
    const [clubChoisi, setClubChoisi] = useState<string | null>(null);
    /* LA DISTANCE SAISIE À LA MAIN.
     *
     * L'application ne connaît la distance qu'au DÉPART : c'est la seule qui
     * soit écrite au carnet. Dès le deuxième coup, elle ne sait plus rien et
     * se tait — ce qui est honnête, mais laisse le joueur sans avis précisément
     * là où le choix du club est le moins évident.
     *
     * Le joueur, lui, sait ce qu'il reste : il lit un piquet, une plaquette de
     * fairway, ou son télémètre. Il la donne, l'application répond. */
    const [cibleSaisie, setCibleSaisie] = useState<number | null>(null);
    const [conseilOuvert, setConseilOuvert] = useState(false);
    /** Le club MIS EN MAIN par le conseil. On le retient pour pouvoir dire d'où
     *  il vient une fois la carte refermée — sans quoi le joueur retrouve un
     *  club changé sans savoir qui l'a changé. */
    const [conseilApplique, setConseilApplique] = useState<string | null>(null);
    const [choixOuvert, setChoixOuvert] = useState(false);
    /* Le choix du club en PLEINE PAGE, entre deux coups.
     *
     * Le sac s'ouvrait sous la carte et se refermait au premier tap : rien
     * n'obligeait à le regarder, et on enchaînait trois coups avec le club de
     * la dernière fois sans jamais se poser la question. L'écran s'interpose à
     * la place. La carte reste, mais fermée — elle sert à vérifier ce qu'on a
     * en main, plus à choisir. */
    const [choixEcran, setChoixEcran] = useState(false);
    /* Après un coup joué avec un club, on propose le suivant.
     *
     * C'est le moment où la question se pose vraiment : on vient de frapper,
     * on marche vers la balle, et le prochain coup ne se joue presque jamais
     * avec le même fer. Attendre que le joueur pense à ouvrir le sélecteur,
     * c'est le laisser taper le pavé avec le club d'avant. */
    const [confirmSortie, setConfirmSortie] = useState(false);
    const [trousOuvert, setTrousOuvert] = useState(false);
    const [cartesOuvertes, setCartesOuvertes] = useState(false);
    const [penaliteOuverte, setPenaliteOuverte] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void Promise.all([getCurrentRound(), getBag()])
            .then(([r, b]) => {
                setEtat(r);
                setClubs(b.clubs);
                const joues = r.myHoles.filter((h) => h.strokes != null).length;
                setTrou(Math.min(joues + 1, r.round?.holeCount ?? 18));
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'));
    }, []);

    /* Les scores des autres se rafraîchissent au changement de trou, pas sur
     * une minuterie : un tour dure quatre heures, et un téléphone qui
     * interroge le serveur toutes les trente secondes ne finit pas la partie.
     *
     * Les dépendances sont des PRIMITIVES. `etat.round` est recréé à chaque
     * réponse : le mettre ici relance l'effet, qui refait la requête, qui
     * recrée l'objet — une boucle qui ne s'arrête que quand le serveur refuse. */
    useEffect(() => {
        if (!etat?.round || etat.round.status !== 'open') return;
        void getCurrentRound()
            .then((r) => setEtat((e) => (e == null || r.round == null ? e : r)))
            .catch(() => { /* le jeu continue sans le classement */ });
    }, [trou, etat?.round?.id, etat?.round?.status]);

    /* Une relève, mais BORNÉE à l'attente des réponses.
     *
     * Le refus d'une minuterie tient toujours : un tour dure quatre heures, et
     * un téléphone qui interroge le serveur toutes les trente secondes ne le
     * finit pas. Il ne tient plus à UN moment précis — le premier départ,
     * quand on attend que les autres acceptent. Là, personne ne change de trou,
     * donc rien ne déclenchait le rafraîchissement ci-dessus : l'organisateur
     * restait devant une pile incomplète jusqu'à relancer l'application à la
     * main.
     *
     * On relève donc tant qu'il reste une réponse à recevoir, et on s'arrête
     * dès que tout le monde a répondu. Quelques minutes de sondage au départ,
     * rien pendant les quatre heures qui suivent.
     *
     * SEULS les joueurs sont repris de la réponse. Remplacer l'état entier
     * écraserait les coups saisis à l'instant mais pas encore confirmés par le
     * serveur — on peut très bien jouer le premier trou pendant que les autres
     * répondent. */
    const enAttente = useMemo(
        () => (etat?.players ?? []).some((j) => j.status === 'pending'),
        [etat?.players],
    );

    useEffect(() => {
        if (!etat?.round || etat.round.status !== 'open' || !enAttente) return undefined;
        let vivant = true;
        const relever = (): void => {
            if (document.visibilityState !== 'visible') return;
            void getCurrentRound()
                .then((r) => {
                    if (!vivant || r.round == null) return;
                    setEtat((e) => (e == null ? e : { ...e, players: r.players }));
                })
                .catch(() => { /* le jeu continue sans le classement */ });
        };
        const timer = window.setInterval(relever, ATTENTE_MS);
        document.addEventListener('visibilitychange', relever);
        window.addEventListener('focus', relever);
        return () => {
            vivant = false;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', relever);
            window.removeEventListener('focus', relever);
        };
    }, [etat?.round, enAttente]);

    const hole = etat?.holes.find((h) => h.number === trou) ?? null;
    const carte = etat?.myHoles.find((h) => h.holeNumber === trou) ?? null;
    /* Le total du trou : les coups joués PLUS ce qu'ils ont coûté.
     *
     * C'est le chiffre de la carte de score, et c'est celui que le joueur
     * annonce au départ suivant. Le détail — quel coup a coûté, et pourquoi —
     * vit sur les coups eux-mêmes : quand on demande d'où vient un « +1 », la
     * réponse doit être dans la ligne du coup, pas dans un total qu'il faudrait
     * reconstituer. */
    const coups = carte?.strokes ?? 0;
    const putts = carte?.putts ?? 0;
    const histoire = etat?.holeHistory.find((h) => h.holeNumber === trou) ?? null;

    /* Le ruban des coups reste collé à sa fin.
     *
     * Sur un trou qui s'allonge — huit, dix coups — la rangée déborde de
     * l'écran, et c'est la DROITE qui déborde : le coup qu'on vient de jouer,
     * et la corbeille qui le défait. Or c'est exactement le moment où l'on a
     * besoin d'elle, un appui de travers arrivant surtout quand ça se
     * complique. Il fallait faire glisser la rangée du doigt pour la
     * retrouver, ganté, en marchant.
     *
     * Elle se recale donc à chaque coup, et au changement de trou où elle
     * revient au début avec le compteur. */
    const rubanCoups = useRef<HTMLDivElement>(null);
    useEffect(() => { setCoupAuBilan(null); setFeteVue(false); }, [trou]);


    useEffect(() => {
        const r = rubanCoups.current;
        if (r == null) return;
        const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        r.scrollTo({ left: r.scrollWidth, behavior: doux ? 'smooth' : 'auto' });
    }, [coups, trou]);
    const sac = useMemo(() => listeDesClubs(clubs), [clubs]);
    const coupsDuTrou = useMemo(
        () => (etat?.myShots ?? []).filter((s) => s.holeNumber === trou)
            .sort((a, b) => a.shotIndex - b.shotIndex),
        [etat?.myShots, trou],
    );
    /* Le coup dont on veut la raison, par son rang dans le trou.
     *
     * Un rang et non l'objet : les coups sont remplacés à chaque
     * enregistrement, et garder l'objet aurait figé une version périmée du
     * coup au premier « défaire ». */
    const [coupDetaille, setCoupDetaille] = useState<number | null>(null);

    /* Le coup dont on regarde le bilan, par son rang.
     *
     * Ouvert à l'instant où le pavé enregistre, fermé par « Coup suivant » ou
     * par la balle rentrée. Un rang et non l'objet : le coup est réécrit à
     * chaque correction, et garder l'objet aurait figé une version périmée. */
    const [coupAuBilan, setCoupAuBilan] = useState<number | null>(null);

    /* La fête du trou terminé a-t-elle déjà été vue ?
     *
     * Un booléen et non une déduction de `trouFini` : sinon l'écran se
     * remonterait à chaque retour sur un trou déjà joué — on revient corriger
     * une saisie, pas pour refaire la fête. Il se remet à zéro au changement
     * de trou, avec le reste. */
    const [feteVue, setFeteVue] = useState(false);

    /** Ce que les pénalités ajoutent au trou en cours.
     *
     *  Deux origines, un seul total : celles qui viennent d'un coup — un
     *  dégagement, un hors-limites — et celles qui n'en viennent pas, posées
     *  sur le trou. Le joueur, lui, ne compte que des coups. */
    const penalitesTrou = useMemo(() => carte?.penalties ?? [], [carte]);
    const coutPenalitesTrou = useMemo(
        () => holePenaltyTotal(penalitesTrou), [penalitesTrou],
    );
    const penalites = useMemo(
        () => coupsDuTrou.reduce((n, s) => n + (s.penaltyStrokes ?? 0), 0)
            + coutPenalitesTrou,
        [coupsDuTrou, coutPenalitesTrou],
    );
    const detail = coupDetaille == null
        ? null
        : coupsDuTrou.find((s) => s.shotIndex === coupDetaille) ?? null;
    const bilan = coupAuBilan == null
        ? null
        : coupsDuTrou.find((s) => s.shotIndex === coupAuBilan) ?? null;


    /* La distance à couvrir : celle du carnet au départ, celle du joueur ensuite.
     *
     * La saisie l'emporte même sur le départ : on peut jouer court volontairement
     * — un par 5 qu'on découpe, un dogleg qu'on ne coupe pas. */
    const cibleM = cibleSaisie ?? (coups === 0 ? hole?.distanceM ?? null : null);

    const suggestion = useMemo(
        () => (cibleM != null ? clubPour(clubs, cibleM) : null),
        [clubs, cibleM],
    );

    /* CE QU'ON A JOUÉ ICI LA DERNIÈRE FOIS.
     *
     * Le coup de MÊME RANG, et rien d'autre : au troisième coup on montre le
     * troisième coup d'alors. Dès que le trou se joue autrement les séquences
     * divergent, et repcher le coup de départ pour le présenter comme « ce
     * que vous aviez fait » serait un faux souvenir. Quand le rang n'existait
     * pas — le trou s'était joué en moins de coups — on se tait. */
    const passageTrou = useMemo(
        () => (etat?.dernierPassage?.shots ?? [])
            .filter((x) => x.holeNumber === trou)
            .sort((a, b) => a.shotIndex - b.shotIndex),
        [etat?.dernierPassage, trou],
    );
    const passageCoup = passageTrou.find((x) => x.shotIndex === coups + 1) ?? null;

    /* La distance saisie vaut pour UN coup, jamais deux.
     *
     * Une fois la balle partie, il en reste autre chose — et laisser l'ancienne
     * sous les yeux ferait conseiller un club pour une distance qui n'existe
     * plus. On l'oublie donc au coup suivant comme au trou suivant : se taire
     * coûte une saisie, se tromper coûte un coup. */
    useEffect(() => {
        setCibleSaisie(null);
        setConseilOuvert(false);
        setConseilApplique(null);
    }, [trou, coups]);
    const clubMemoire = passageCoup
        ? clubs.find((c) => c.id === passageCoup.clubId) ?? null
        : null;
    /* Quel club est en main, dans l'ordre de ce qui fait autorite :
     *
     *   1. celui que le joueur vient de designer ;
     *   2. la suggestion, qui n'existe que sur le depart ;
     *   3. le DERNIER club joue — sur ce trou, sinon sur le tour.
     *
     * Sans le troisieme, rouvrir un trou deja entame laissait l'ecran sans
     * club, donc le pave desactive : plus rien a toucher sur la seule zone
     * qu'on touche. Or on revient toujours sur un trou en cours avec un club
     * a la main, et c'est celui d'avant tant qu'on n'en change pas. */
    const dernierJoue = useMemo(() => {
        const ici = coupsDuTrou[coupsDuTrou.length - 1]?.clubId;
        if (ici) return ici;
        const partout = [...(etat?.myShots ?? [])]
            .sort((a, b) => a.holeNumber - b.holeNumber || a.shotIndex - b.shotIndex)
            .filter((x) => x.clubId != null);
        return partout[partout.length - 1]?.clubId ?? null;
    }, [coupsDuTrou, etat?.myShots]);

    const clubCourant = clubs.find((c) => c.id === clubChoisi)
        ?? suggestion?.club
        ?? clubs.find((c) => c.id === dernierJoue)
        ?? null;
    /* Le club en main est-il celui qu'on propose ?
     *
     * « Aucun choix manuel » ne suffit plus comme condition : depuis que le
     * conseil MET le club en main, `clubChoisi` est rempli par le conseil
     * lui-même. Sans le second cas, le jeton et son explication disparaîtraient
     * au moment précis où l'on vient de les demander. */
    const estPropose = clubCourant != null && suggestion?.club.id === clubCourant.id
        && (clubChoisi === null || conseilApplique === clubCourant.id) && suggestion.sur;
    const estPutt = clubCourant?.category === 'putter';
    /* Le taux vient du sac CLASSÉ, pas de la liste brute : c'est `listeDesClubs`
     * qui le calcule, une fois, pour toutes les tuiles. Le relire ici plutôt
     * que le recalculer garantit que la carte et la tuile du même club
     * affichent le même chiffre. */
    const fiabilite = sac.find((c) => c.id === clubCourant?.id)?.rate ?? null;


    /* Le trou est fini quand un coup a mis la balle au fond, et c'est le
     * JOUEUR qui le dit — sur le bilan du coup, ou par « Rentré » au putter.
     *
     * On le devinait auparavant : dernier coup joué au putter et centré.
     * C'était la seule fin dont on pouvait être à peu près sûr, et elle
     * manquait tout le reste — un chip rentré du bord du green, un coup de
     * bunker qui tombe dedans, un long putt de la frange joué au fer. */
    const releve = carte?.pickedUp === true;
    const trouFini = useMemo(
        () => releve || coupsDuTrou.some((s) => s.holed),
        [coupsDuTrou, releve],
    );

    /* Arriver sur un trou AVANT le premier coup ouvre l'écran de choix.
     *
     * C'est le moment que le joueur nous a demandé de rendre incontournable :
     * on quitte un green, on arrive au départ suivant, et la question est
     * « avec quoi ». C'est aussi là que la suggestion existe — elle ne vaut que
     * sur le départ — donc le seul endroit où la tuile dorée a quelque chose à
     * dire.
     *
     * La condition n'est PAS « aucun club en main » : la suggestion remplit
     * déjà cette case, si bien qu'un départ conseillé n'ouvrait jamais l'écran
     * — exactement le cas où il sert le plus.
     *
     * UNE FOIS par trou : sans ce garde, fermer sans rien choisir rouvrait
     * aussitôt, et la sortie n'en était plus une. On rappelle, on n'enferme
     * pas. */
    const choixPropose = useRef<number | null>(null);
    useEffect(() => {
        if (!etat?.round || trouFini || estPutt || coups > 0) return;
        if (choixPropose.current === trou) return;
        choixPropose.current = trou;
        setChoixEcran(true);
    }, [etat?.round, trou, trouFini, estPutt, coups]);

    /* Combien d'AUTRES sont de la partie, invitations comprises.
     *
     * Sert a deux choses : dire au moment de quitter que la partie continue
     * sans nous, et decider si le bandeau de classement a lieu d'etre. */
    const partenaires = (etat?.players ?? [])
        .filter((j) => j.userId !== moi && j.status !== 'declined').length;

    const classement = useMemo(() => {
        // Les INVITES figurent aussi, en attente : l'organisateur doit voir
        // qui il a convie des le premier depart, pas seulement ceux qui ont
        // deja repondu. Ils passent en couleur en rejoignant.
        const joueurs = (etat?.players ?? []).filter((j) => j.status !== 'declined');
        const parCumule = (n: number): number => (etat?.holes ?? [])
            .filter((h) => h.number <= n)
            .reduce((somme, h) => somme + (h.par ?? 0), 0);
        return joueurs
            .map((j) => ({
                ...j,
                ecart: j.totalStrokes != null && j.holesPlayed > 0
                    ? j.totalStrokes - parCumule(j.holesPlayed)
                    : null,
            }))
            .sort((a, b) => {
                // Qui n'a pas encore repondu ferme la marche, derriere ceux qui
                // sont partis mais n'ont rien marque.
                const rang = (j: { status: string; ecart: number | null }): number =>
                    j.status !== 'accepted' ? 2 : j.ecart == null ? 1 : 0;
                if (rang(a) !== rang(b)) return rang(a) - rang(b);
                if (a.ecart == null || b.ecart == null) return 0;
                return a.ecart - b.ecart;
            });
    }, [etat?.players, etat?.holes]);

/* Le joueur dont on est le plus proche au classement, et de combien.
     *
     * Le plus proche et non le premier : sur une partie à quatre, savoir qu'on
     * est à douze coups du meilleur n'apprend rien, alors qu'un coup d'écart
     * avec son voisin de tableau est exactement ce qui fait lever les yeux. */
    const voisin = useMemo(() => {
        const moiLigne = classement.find((j) => j.userId === moi);
        if (moiLigne?.ecart == null) return null;
        const autres = classement.filter(
            (j) => j.userId !== moi && j.status === 'accepted' && j.ecart != null,
        );
        if (autres.length === 0) return null;
        const proche = autres.reduce((a, b) => (
            Math.abs(b.ecart! - moiLigne.ecart!) < Math.abs(a.ecart! - moiLigne.ecart!) ? b : a
        ));
        return {
            nom: proche.displayName.split(' ')[0] ?? proche.displayName,
            ecart: proche.ecart! - moiLigne.ecart!,
        };
    }, [classement, moi]);

    const majCarte = async (
        nouveaux: number, nouveauxPutts: number, releve = false,
        motifs: HolePenalty[] = penalitesTrou,
    ): Promise<void> => {
        if (!etat?.round) return;
        await saveHole(etat.round.id, trou, {
            strokes: nouveaux === 0 ? null : nouveaux,
            putts: nouveaux === 0 ? null : nouveauxPutts,
            pickedUp: releve,
            penalties: motifs,
        });
        setEtat((e) => (e == null ? e : {
            ...e,
            myHoles: [
                ...e.myHoles.filter((h) => h.holeNumber !== trou),
                {
                    holeNumber: trou,
                    strokes: nouveaux === 0 ? null : nouveaux,
                    putts: nouveaux === 0 ? null : nouveauxPutts,
                    pickedUp: releve,
                    penalties: motifs,
                },
            ].sort((a, b) => a.holeNumber - b.holeNumber),
        }));
    };

    /** Relever la balle : on s'arrête là, et on passe au trou suivant.
     *
     *  Le mot est celui du golf. On ne « abandonne » pas un trou, on relève sa
     *  balle — c'est le geste, et c'est ce que disent les joueurs.
     *
     *  Le score retenu est celui déjà joué, avec un plancher au double bogey :
     *  on ne relève pas une balle en étant sous le par, et le plancher évite
     *  qu'un trou abandonné se retrouve compté mieux qu'un trou fini. Le
     *  drapeau `pickedUp` dit à la carte et aux statistiques que ce n'est pas
     *  un score joué jusqu'au fond — sans lui, un 5 relevé mentirait par
     *  omission. */
    const relever = async (): Promise<void> => {
        if (etat?.round == null) return;
        setBusy(true);
        setError(null);
        try {
            await majCarte(Math.max(coups, (hole?.par ?? 3) + 2), putts, true);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    /* Poser une pénalité qui ne vient d'aucun coup.
     *
     * Elle change le total du trou sans rien ajouter au ruban des coups : il
     * n'y a pas eu de balle frappée, et en inventer une pour porter le chiffre
     * fausserait tout ce qui se lit depuis les coups — la zone d'arrivée, le
     * club, la fiabilité. Un beau coup de départ suivi d'une inattention dans
     * le bunker serait compté comme un coup raté. */
    const poserPenalite = async (motif: HolePenalty): Promise<void> => {
        if (!etat?.round) return;
        setBusy(true);
        setError(null);
        try {
            await majCarte(
                coups + holePenaltyStrokes(motif), putts, carte?.pickedUp ?? false,
                [...penalitesTrou, motif],
            );
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    /* Et la retirer : une pénalité se pose d'un tap, elle doit se reprendre du
     * même — on se trompe de ligne, ou l'on découvre au trou suivant qu'il n'y
     * avait pas faute. */
    const retirerPenalite = async (index: number): Promise<void> => {
        if (!etat?.round) return;
        const motif = penalitesTrou[index];
        if (motif == null) return;
        setBusy(true);
        setError(null);
        try {
            await majCarte(
                Math.max(0, coups - holePenaltyStrokes(motif)), putts,
                carte?.pickedUp ?? false,
                penalitesTrou.filter((_, i) => i !== index),
            );
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    /** Un appui sur le pavé : le coup compte pour la carte ET note ce que la
     *  balle a fait.
     *
     *  Deux formes selon l'endroit d'où l'on joue. Sur le green le putter ne
     *  demande qu'une chose — rentrée ou pas — et c'est `outcome` qui la porte.
     *  Ailleurs on note la ZONE où la balle est tombée, et le motif de pénalité
     *  s'il y en a un. Le coût, lui, n'est jamais transmis : le serveur le
     *  déduit du motif, sans quoi deux versions du client finiraient par ne
     *  plus compter pareil. */
    const enregistrerCoup = async (
        quoi: { landing: ShotLanding | null; holed?: boolean },
    ): Promise<void> => {
        if (!etat?.round) return;
        setBusy(true);
        setError(null);
        /* La pénalité que la zone appelle d'office — dégagement dans l'eau, coup
         * et distance hors-limites. C'est la règle qui suit le plus souvent, et
         * le bilan permet de la retirer d'un tap quand ce n'est pas le cas.
         * Un putt ne l'appelle jamais : sur le green il n'y a rien à franchir. */
        /* Pas de zone, pas de pénalité d'office : la règle se déduit de l'endroit
           où la balle est tombée, et on ne le sait pas encore. Elle sera posée sur
           l'écran suivant, en même temps que la zone. */
        const penalty = estPutt || quoi.landing == null ? null : defaultPenalty(quoi.landing);
        const n = coups + 1 + penaltyStrokes(penalty);
        const rang = coupsDuTrou.length + 1;
        try {
            await majCarte(n, estPutt ? putts + 1 : putts);
            const shot = {
                holeNumber: trou,
                shotIndex: rang,
                clubId: clubCourant?.id ?? null,
                suggestedClubId: estPutt ? null : suggestion?.club.id ?? null,
                outcome: null,
                landing: quoi.landing,
                penaltyReason: penalty,
                holed: quoi.holed ?? false,
            };
            await saveShot(etat.round.id, shot);
            setEtat((e) => (e == null ? e : {
                ...e,
                myShots: [
                    ...e.myShots.filter(
                        (s) => !(s.holeNumber === trou && s.shotIndex === shot.shotIndex),
                    ),
                    {
                        ...shot,
                        penaltyStrokes: penaltyStrokes(penalty),
                        remainingM: null,
                    },
                ],
            }));
            /* Le bilan s'ouvre sur les coups pleins seulement. Un putt ne se
               commente pas : la balle est rentrée ou elle ne l'est pas, et
               c'est déjà ce que les deux boutons demandent. */
            if (!estPutt && !(quoi.holed ?? false)) setCoupAuBilan(rang);
            // Le club RESTE choisi apres le coup. Le remettre a zero rendait
            // le pave inerte des la deuxieme balle : la suggestion ne vaut que
            // pour le depart, donc plus rien n'etait selectionne. Il se
            // reinitialise au changement de trou, ou la suggestion reprend.
            if (clubCourant) setClubChoisi(clubCourant.id);
            // Le tiroir ne s'ouvre plus : le choix du club a son écran, et il
            // s'affiche après le bilan du coup. Deux endroits pour le même
            // geste, c'est un de trop — et le tiroir gagnait toujours, puisqu'il
            // s'ouvrait tout seul.
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Enregistrement impossible');
        } finally { setBusy(false); }
    };

    /** Retoucher le coup qu'on regarde : sa zone, sa pénalité, ou le fait que
     *  la balle soit tombée dedans.
     *
     *  C'est le MÊME coup qu'on réécrit — `POST /shots` est un upsert sur
     *  (trou, index) — donc aucun second coup n'apparaît. Seul le total du trou
     *  bouge, et seulement si la pénalité change. */
    const retoucherCoup = async (
        patch: { landing?: ShotLanding; penaltyReason?: ShotPenalty | null; holed?: boolean },
        /* Le coup visé, quand ce n'est pas celui du bilan : le bilan de TROU
         * rouvre le trou en retirant le drapeau d'un coup qu'il désigne
         * lui-même. */
        rang?: number,
    ): Promise<void> => {
        if (etat?.round == null) return;
        const cible = rang ?? coupAuBilan;
        if (cible == null) return;
        const round = etat.round;
        const avant = coupsDuTrou.find((s) => s.shotIndex === cible);
        if (avant == null) return;
        setBusy(true);
        setError(null);
        /* Changer de zone peut changer la pénalité qui va avec : passer de l'eau
         * au rough n'a plus de raison de coûter un dégagement. On repose donc
         * celle de la nouvelle zone, sauf si le joueur en a explicitement
         * demandé une autre dans le même geste. */
        const zone = patch.landing ?? avant.landing ?? 'fairway';
        const motif = 'penaltyReason' in patch
            ? patch.penaltyReason ?? null
            : patch.landing != null
                ? defaultPenalty(patch.landing)
                : avant.penaltyReason;
        const corrige = {
            holeNumber: avant.holeNumber,
            shotIndex: avant.shotIndex,
            clubId: avant.clubId,
            suggestedClubId: avant.suggestedClubId,
            outcome: null,
            landing: zone,
            penaltyReason: motif ?? null,
            holed: patch.holed ?? avant.holed,
        };
        try {
            await saveShot(round.id, corrige);
            const ecart = penaltyStrokes(motif ?? null) - (avant.penaltyStrokes ?? 0);
            if (ecart !== 0) await majCarte(Math.max(1, coups + ecart), putts);
            setEtat((e) => (e == null ? e : {
                ...e,
                myShots: e.myShots.map((sh) => (
                    sh.holeNumber === corrige.holeNumber && sh.shotIndex === corrige.shotIndex
                        ? { ...sh, ...corrige, penaltyStrokes: penaltyStrokes(motif ?? null) }
                        : sh
                )),
            }));
            // La balle est au fond : le bilan n'a plus lieu d'être, l'écran de
            // trou terminé prend la suite.
            if (patch.holed === true) setCoupAuBilan(null);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Correction impossible');
        } finally { setBusy(false); }
    };

    /** Défaire le dernier coup. Se tromper d'un appui arrive à chaque trou. */
    const defaire = async (): Promise<void> => {
        if (coups === 0 || etat?.round == null) return;
        const round = etat.round;
        setBusy(true);
        setError(null);
        try {
            const dernier = coupsDuTrou[coupsDuTrou.length - 1];
            if (dernier == null) return;
            const etaitPutt = clubs.find((c) => c.id === dernier.clubId)?.category === 'putter';
            /* Le coup ET sa pénalité partent ensemble : un hors-limites défait
               rend deux coups à la carte, pas un. Les décompter séparément
               laisserait un « +1 » orphelin qu'on ne saurait plus rattacher à
               rien. */
            const rendus = 1 + (dernier.penaltyStrokes ?? 0);
            await deleteShot(round.id, trou, dernier.shotIndex);
            const restePutts = etaitPutt
                ? Math.max(0, putts - 1)
                : Math.min(putts, Math.max(0, coups - rendus));
            await majCarte(Math.max(0, coups - rendus), restePutts);
            setEtat((e) => (e == null ? e : {
                ...e,
                myShots: e.myShots.filter(
                    (s) => !(s.holeNumber === trou && s.shotIndex === dernier.shotIndex),
                ),
            }));
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Correction impossible');
        } finally { setBusy(false); }
    };

    const terminer = async (): Promise<void> => {
        if (!etat?.round) return;
        setBusy(true);
        const id = etat.round.id;
        try {
            await finishRound(id);
            // Droit à la carte : c'est ce qu'on veut voir en rendant le score,
            // et c'est là qu'on corrige le trou mal compté qu'on découvre en
            // refaisant l'addition.
            navigate(`/parcours/tour/${id}`);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de terminer');
            setBusy(false);
        }
    };

    const annuler = async (): Promise<void> => {
        if (!etat?.round) return;
        setBusy(true);
        try {
            await deleteRound(etat.round.id);
            navigate('/parcours');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Annulation impossible');
            setBusy(false);
        }
    };

    if (error && !etat) {
        return <div role="alert" className="alert alert-error m-4 text-sm"><span>{error}</span></div>;
    }
    if (!etat) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }
    if (!etat.round) {
        return (
            <div className="space-y-4 py-8 text-center">
                <p className="text-lg font-semibold">Aucun parcours en cours.</p>
                <button type="button" onClick={() => navigate('/parcours')}
                    className="btn btn-primary h-14 gap-2 tap-target">
                    <Flag size={18} /> Choisir un parcours
                </button>
            </div>
        );
    }

    const nbTrous = etat.round.holeCount;
    const total = etat.myHoles.reduce((s, h) => s + (h.strokes ?? 0), 0);
    const trousJoues = etat.myHoles.filter((h) => h.strokes != null).length;
    const parJoue = etat.holes
        .filter((h) => etat.myHoles.some((m) => m.holeNumber === h.number && m.strokes != null))
        .reduce((s, h) => s + (h.par ?? 0), 0);

    return (
        /* Colonne pleine hauteur, comme au practice : le pavé prend tout ce que
         * les étages du dessus lui laissent. `dvh` et non `vh` — sur mobile la
         * barre d'URL se rétracte et `vh` reste figé sur la hauteur dépliée.
         *
         * `text-base-content` referme le blanc du fond fairway, peint par
         * AppShell sur cet écran : sans lui, chaque carte claire en dessous
         * hériterait d'un texte blanc — blanc sur blanc. Seul l'en-tête, qui
         * pose directement sur le fond, écrit en clair, et il le dit. */
        <div className="flex min-h-[calc(100dvh-8.5rem)] flex-col gap-2 py-2 text-base-content">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h1 className="font-display truncate text-xl font-bold tracking-tight text-white">
                        {etat.round.courseName}
                    </h1>
                    {/* L'écart au par porte SA couleur — rouge au-dessus, vert
                        dessous — le reste de la ligne reste blanc : c'est le
                        seul chiffre de cette ligne qui soit un jugement.

                        Les deux teintes sont CLAIRES et non les `text-error` /
                        `text-success` du thème : ceux-ci sont taillés pour
                        écrire sur du papier et tomberaient sous 2:1 sur le
                        fond fairway. */}
                    <p className="text-xs font-bold tabular-nums text-white/85">
                        {total} coup{total > 1 ? 's' : ''}
                        <span className="text-white/40">{' · '}</span>
                        {trousJoues}/{etat.round.holeCount} trous
                        {parJoue > 0 && (
                            <>
                                <span className="text-white/40">{' · '}</span>
                                <span className={total > parJoue ? 'text-[oklch(72%_0.16_25)]'
                                    : total < parJoue ? 'text-[oklch(80%_0.16_150)]' : 'text-white/70'}>
                                    {total === parJoue ? 'au par'
                                        : `${total > parJoue ? '+' : '−'}${Math.abs(total - parJoue)} au par`}
                                </span>
                            </>
                        )}
                    </p>
                </div>
                {/* Le meilleur coin de l'écran ne sert plus à sortir.
                    « Terminer » s'y touchait une fois par tour et y passait
                    quatre heures ; il est descendu en bas de page, où l'on
                    sort. À sa place, ce qu'on regarde vraiment quand on joue à
                    plusieurs : qui est devant. */}
                <PileAvatars lignes={classement} moi={moi}
                    onOuvrir={() => setCartesOuvertes(true)} />
            </div>

            {error && (
                <div role="alert" className="alert alert-error text-sm">
                    <span>{error}</span>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={() => setError(null)}>OK</button>
                </div>
            )}

            {/* Le classement REMONTE au-dessus des trois étapes.

                Il était collé au pavé, dans le champ de vision de la zone
                qu'on touche — bonne raison, mais il s'insérait alors entre
                l'étape 2 et l'étape 3 et cassait la seule chose que ces trois
                cartes servent à montrer : leur ordre. Il n'appartient
                d'ailleurs à aucune des trois, puisqu'il ne parle ni du trou,
                ni du sac, ni du coup, mais des autres joueurs. Sa place est
                avant la séquence, pas au milieu. */}

            {/* Le bandeau de tête : LE TOUR, sur le panneau vert.

                Il portait les quatre valeurs du trou courant. Elles ont
                descendu d'un cran, dans l'étape « Trou n/N », et le ruban des
                dix-huit trous a pris leur place — l'inverse exact de la
                disposition précédente.

                Ce que le joueur cherche en levant les yeux, c'est où il en est
                de son TOUR : le total, l'écart au par, la texture des trous
                déjà joués. Le par et la distance du trou courant, eux, servent
                à préparer un coup — ils appartiennent à la séquence, pas à
                l'entête.

                Le ruban bascule en couleurs claires sur ce fond (voir `allure`
                dans Carte.tsx) : les verts et rouges du thème sont taillés pour
                le papier et tombent sous 1,5:1 ici.

                PLUS DE FLÈCHE « trou suivant ». Elle mangeait un cinquième de
                la largeur pour un geste que personne ne fait plus : un trou se
                termine par sa balle rentrée ou relevée, l'écran de fête
                s'affiche, et c'est son bouton « Trou n › » qui fait avancer.
                Passer au trou suivant SANS avoir fini celui-ci n'est pas un
                raccourci, c'est une carte trouée.

                Reste le ruban, qui mène à n'importe quel trou d'un tap : le
                geste juste pour revenir corriger un trou précis. Il prend
                maintenant toute la largeur, et ses cases respirent d'autant. */}
            <div className="flex items-stretch">
                {/* Le panneau est vert profond, comme au mockup, mais il vit
                    SUR le fond fairway : à 32 % de clarté contre 30 % pour le
                    haut du dégradé, il s'y dissolvait. Une arête claire le
                    redécoupe — c'est la frontière qui porte la limite, pas
                    l'écart de fond, exactement la règle posée dans index.css. */}
                <div className="min-w-0 flex-1 rounded-field border border-white/15 bg-[var(--fairway-deep)] px-2 py-1.5 shadow-md">
                    {/* Toucher une case OUVRE LA LISTE, elle ne change plus de
                        trou directement.

                        Le ruban fait trente pixels par case et se tient sous
                        le pouce, en haut de l'écran, là où la main tient déjà le
                        téléphone. Un frottement suffisait à basculer sur un
                        autre trou, et le basculement était DISCRET : même
                        forme, mêmes couleurs, seuls quelques chiffres
                        changeaient. Un joueur pouvait enregistrer deux coups
                        sur le mauvais trou sans rien remarquer.

                        La modale, elle, se voit. Un appui accidentel se solde
                        par une boîte à fermer, pas par une carte faussée — et
                        le geste volontaire n'y perd qu'un tap, contre une
                        liste où chaque trou porte son par, sa distance et son
                        score, de quoi choisir en connaissance de cause.

                        La case tapée n'est pas transmise : la liste s'ouvre
                        entière, avec le trou courant marqué. Elle ne présume
                        pas de ce qu'on visait. */}
                    <Carte nu sombre holes={etat.holes} myHoles={etat.myHoles} trouCourant={trou}
                        rendus={mesRendus} net={scoreNet}
                        onChoisir={() => setTrousOuvert(true)} />
                </div>
            </div>

            {/* ① TROU n/N — ce qu'il y a à savoir avant de jouer.

                Le titre porte le numéro et le rang : « Trou 3/9 » dit d'un coup
                où l'on est et combien il en reste, ce que le seul chiffre du
                panneau vert ne disait pas. Les trois autres valeurs — par,
                distance, index — le suivent, en encre sur le papier de la
                carte plutôt qu'en blanc sur le vert.

                Puis les coups, MONTRÉS DÈS LE DÉPART. Voir plus bas pourquoi. */}
            {/* Plus de bouton « Tous les trous » ici : le ruban du bandeau vert
                y mène désormais, et c'est le geste évident — on touche la liste
                des trous pour ouvrir la liste des trous. Deux portes vers la
                même boîte, dont une nommée et l'autre non, c'était une porte de
                trop dans un en-tête qui doit rester une étiquette. */}
            <Etape n={1} titre={`Trou ${trou}/${etat.round.holeCount}`}>
                <div className="grid grid-cols-3 gap-1 text-center">
                    <Fait mot="par" v={hole?.par != null ? String(hole.par) : '—'} />
                    <Fait mot="distance" v={hole?.distanceM != null ? `${hole.distanceM} m` : '—'} />
                    <Fait mot="index" v={hole?.strokeIndex != null ? String(hole.strokeIndex) : '—'} />
                </div>

                {/* Les coups du trou — visibles DÈS LE PREMIER REGARD.

                    La rangée n'apparaissait qu'au premier coup enregistré :
                    l'écran changeait alors de forme sous le pouce, et surtout
                    rien n'avait annoncé où l'information allait s'inscrire. Le
                    joueur tapait « Bon contact » sans savoir où regarder pour
                    vérifier que c'était pris.

                    Elle est donc là dès le départ, sous forme de cases vides —
                    autant que le par, parce que le par est littéralement le
                    nombre de coups attendus sur ce trou. Chaque case porte son
                    rang et se remplit à son tour. Ce n'est pas une consigne :
                    au-delà du par les coups continuent de s'ajouter, dans la
                    brique des ratés, et le compteur à gauche dit le
                    dépassement.

                    UNE rangée, qui défile. En passant à la ligne, cinq coups
                    doublaient la hauteur de la carte et poussaient le pavé sous
                    l'écran. Un trou long doit coûter un glissement du doigt, pas
                    la zone qu'on touche à chaque balle. */}
                <div className="mt-1.5 border-t border-base-300 pt-1.5">
                    {/* Le compte du trou, et ce qu'il coûte.

                        Le dépassement s'écrit EN ROUGE contre le par, là où l'œil
                        compare déjà les deux chiffres. Le nommer — « bogey »,
                        « double » — obligeait à traverser l'écran pour apprendre
                        ce que « 4 / 3 » disait tout seul. Le nom du score
                        n'apparaît qu'une fois la balle au fond, sur le bouton du
                        trou suivant : c'est le seul moment où il est vrai.

                        À droite vient ce que l'écran est SEUL à savoir : ce que ce
                        trou vous coûte d'habitude. Le par dit ce que ferait un
                        bon joueur, la moyenne dit ce que vous faites, vous. */}
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="font-score text-2xl leading-none">
                            {coups}
                            {hole?.par != null && (
                                <>
                                    <span className="text-base font-normal opacity-50">{' / '}{hole.par}</span>
                                    {coups > hole.par && (
                                        <span className="ml-1 text-base font-bold text-error">
                                            +{coups - hole.par}
                                        </span>
                                    )}
                                </>
                            )}
                        </span>
                        {/* Les pénalités passent DEVANT la moyenne et devant les
                            putts : c'est le chiffre qui explique un total qui
                            ne tombe pas juste, et c'est donc celui qu'on
                            cherche en premier quand on doute. Le détail —
                            lequel des coups, et pour quelle règle — est à un
                            tap sur la pastille correspondante. */}
                        <span className="text-right text-xs opacity-70">
                            {penalites > 0 ? (
                                <span className="font-bold" style={{ color: 'var(--brique-encre)' }}>
                                    dont {penalites} pénalité{penalites > 1 ? 's' : ''}
                                </span>
                            ) : histoire?.moyenne != null ? (
                                <>
                                    <span className="opacity-70">votre moyenne ici </span>
                                    <span className="font-bold tabular-nums">
                                        {histoire.moyenne.toFixed(1).replace('.', ',')}
                                    </span>
                                    <span className="opacity-70">
                                        {' '}sur {histoire.parties} tour{histoire.parties > 1 ? 's' : ''}
                                    </span>
                                </>
                            ) : putts > 0 ? (
                                <span className="opacity-60">
                                    dont {putts} putt{putts > 1 ? 's' : ''}
                                </span>
                            ) : null}
                        </span>
                    </div>

                    <div ref={rubanCoups}
                        className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5 scroll-smooth
                                   [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {Array.from({ length: Math.max(coupsDuTrou.length, hole?.par ?? 0, 1) }, (_, i) => {
                            const s = coupsDuTrou.find((x) => x.shotIndex === i + 1);
                            /* Le rang, un filet, puis la marque.
                             *
                             * Le filet vertical n'est pas décoratif : sans lui le
                             * chiffre du rang et la marque se lisent comme un
                             * seul bloc, et « 1✓ » ressemble à un score.
                             *
                             * Le rang est à pleine encre, et c'est la TAILLE qui
                             * le met au second plan. À 60 % d'opacité il tombait
                             * à 2,05:1 sur la brique — un chiffre de dix pixels
                             * qu'on ne lit plus au soleil. */
                            const rang = (
                                <>
                                    <span className="text-[10px] font-bold leading-none tabular-nums">
                                        {i + 1}
                                    </span>
                                    <span aria-hidden="true" className="h-3.5 w-px bg-current opacity-25" />
                                </>
                            );
                            if (s == null) {
                                /* Une case en attente : elle montre OÙ le coup
                                   viendra s'inscrire sans prétendre savoir ce
                                   qu'il sera. Autant que le par, parce que le par
                                   est littéralement le nombre de coups attendus. */
                                return (
                                    <span key={i} aria-hidden="true"
                                        className="flex h-7 shrink-0 items-center gap-1.5 rounded-field border
                                                   border-dashed border-base-300 px-2 text-base-content/45">
                                        {rang}
                                        <span className="text-xs font-bold leading-none">–</span>
                                    </span>
                                );
                            }
                            /* La pastille porte la COULEUR DE LA ZONE, celle-là
                               même que le bouton qu'on vient de toucher. Le ruban
                               se lit alors comme un parcours : deux fairways, un
                               bunker, un green. Le mot n'y tient pas à seize
                               pixels, la couleur oui.
                               Un putt n'a pas de zone — la balle est sur le green
                               par définition : il garde le vert d'action et son
                               check laiton, ou la brique et sa croix. */
                            const zone = s.landing;
                            /* Un putt garde son nom. Sa zone est le green par
                               définition, et l'écrire trois fois de suite en
                               fin de trou ne dirait rien de plus que « je suis
                               sur le green », qu'on sait déjà. */
                            const putt = clubs.find((c) => c.id === s.clubId)?.category === 'putter';
                            const habit = zone != null
                                ? {
                                    background: `var(--zone-${zone})`,
                                    color: `var(--zone-${zone}-encre)`,
                                    border: `2px solid color-mix(in oklab, var(--zone-${zone}) 72%, black)`,
                                }
                                : undefined;
                            const penalise = (s.penaltyStrokes ?? 0) > 0;
                            return (
                                <button key={i} type="button"
                                    onClick={() => setCoupDetaille(
                                        coupDetaille === s.shotIndex ? null : s.shotIndex,
                                    )}
                                    aria-pressed={coupDetaille === s.shotIndex}
                                    aria-label={`Coup ${i + 1}${zone != null ? `, ${SHOT_LANDING_LABELS[zone]}` : ''}`}
                                    style={habit}
                                    className={`flex h-7 shrink-0 items-center gap-1.5 rounded-field px-2 transition-transform active:scale-95 ${
                                        zone == null ? 'bg-base-300' : ''
                                    } ${coupDetaille === s.shotIndex ? 'ring-2 ring-accent ring-offset-1 ring-offset-base-100' : ''}`}>
                                    {rang}
                                    <span className="text-[11px] font-bold leading-none">
                                        {putt ? 'Putt' : zone != null ? SHOT_LANDING_LABELS[zone].slice(0, 3) : '—'}
                                    </span>
                                    {/* La balle est au fond : le drapeau clôt la
                                        ligne, et c'est le seul jeton qui porte
                                        autre chose qu'un lieu. */}
                                    {s.holed && (
                                        <Flag size={14} strokeWidth={3} aria-hidden="true" className="text-accent" />
                                    )}
                                    {/* La pénalité se voit sur le coup qui l'a
                                        provoquée, pas seulement dans le total :
                                        c'est là qu'on la cherche quand le compte
                                        ne tombe pas juste. */}
                                    {penalise && (
                                        <span className="font-score text-sm leading-none">
                                            +{s.penaltyStrokes}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {coups > 0 && (
                            <button type="button" disabled={busy} onClick={() => void defaire()}
                                aria-label="Défaire le dernier coup"
                                className="relative flex h-7 shrink-0 items-center px-2 text-error before:absolute before:-inset-2 before:content-['']">
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>

                    {/* Ce que le coup touché raconte.
                        Un « +1 » sur une pastille est un chiffre qu'on ne sait
                        plus expliquer trois trous plus loin. La ligne dit la
                        RÈGLE qui l'a produit, et pas seulement le coût : c'est
                        la réponse à « pourquoi ça fait 6 ? », la seule question
                        qu'on se pose vraiment en relisant sa carte. */}
                    {detail != null && (
                        <p className="mt-1.5 rounded-field bg-base-200 px-2.5 py-1.5 text-[11px] font-semibold leading-snug">
                            <span className="opacity-60">Coup {detail.shotIndex} · </span>
                            {detail.landing != null ? SHOT_LANDING_LABELS[detail.landing] : 'Coup joué'}
                            {detail.holed && <span className="opacity-60">{' · '}balle au fond</span>}
                            {clubs.find((c) => c.id === detail.clubId) != null && (
                                <span className="opacity-60">
                                    {' · '}{clubs.find((c) => c.id === detail.clubId)?.label}
                                </span>
                            )}
                            {detail.penaltyReason != null && (
                                <span className="block" style={{ color: 'var(--brique-encre)' }}>
                                    +{detail.penaltyStrokes} — {SHOT_PENALTY_LABELS[detail.penaltyReason]}
                                </span>
                            )}
                        </p>
                    )}
                </div>
            </Etape>

            {/* ② CHOIX DU CLUB — avec quoi l'on joue.

                La carte est un module partagé avec le practice. Elle a d'abord
                été écrite ici, puis recopiée là-bas en « à peu près pareil » :
                autre icône, autres polices, tuiles plus grossières. Deux cartes
                qui se ressemblent sans se ressembler sont pires que deux cartes
                franchement différentes — le joueur croit reconnaître et se
                trompe de geste.

                Ce qui reste propre au parcours passe en paramètres : le jeton
                « suggéré », la distance à couvrir, l'écart au club idéal, et le
                panneau qui explique la suggestion. */}
            <ChoixDuClub
                n={2}
                disabled={busy}
                ouvert={choixOuvert}
                onOuvrir={setChoixOuvert}
                courantId={clubCourant?.id ?? null}
                onChoisir={(id) => {
                    setClubChoisi(id);
                    // Choisir soi-même, c'est reprendre la main : le jeton du
                    // conseil n'a plus lieu d'être sur un club qu'on a désigné.
                    setConseilApplique(null);
                    setChoixOuvert(false);
                }}
                clubs={sac.map((c) => ({
                    id: c.id,
                    label: c.label,
                    category: c.category,
                    distance: c.referenceDistanceM != null ? `${c.referenceDistanceM} m` : null,
                    taux: c.rate != null ? Math.round(c.rate) : null,
                    /* L'icône dit QUI a demandé : l'ampoule quand c'est
                       l'application qui propose d'après le carnet, le chapeau
                       quand c'est vous qui avez donné la distance. Deux gestes
                       différents, deux marques différentes. */
                    marque: suggestion?.sur && suggestion.club.id === c.id
                        ? (
                            <MarqueTuile>
                                {cibleSaisie != null
                                    ? <GraduationCap size={12} aria-hidden="true" />
                                    : <Lightbulb size={12} aria-hidden="true" />}
                            </MarqueTuile>
                        )
                        : undefined,
                    /* Le souvenir, même quand il contredit le conseil — surtout
                       quand il le contredit. Deux jetons à deux coins : l'avis
                       à droite, ce qu'on avait fait à gauche. */
                    memoire: clubMemoire?.id === c.id
                        ? <MarqueMemoire landing={passageCoup?.landing ?? null} />
                        : undefined,
                }))}
                /* La distance annoncée, puis la fiabilité mesurée. Ce qu'on
                   cherche à cet endroit, c'est de quoi TRANCHER entre deux clubs
                   voisins : la distance est ce que le joueur croit faire, le
                   taux ce qu'il fait vraiment. */
                sousTitre={estPutt ? 'sur le green' : ([
                    clubCourant?.referenceDistanceM != null
                        ? `${clubCourant.referenceDistanceM} m` : null,
                    fiabilite != null ? `fiabilité ${Math.round(fiabilite)} %` : null,
                ].filter(Boolean).join(' · ') || '—')}
                legende={cibleM != null
                    ? `${cibleM} m à couvrir${cibleSaisie != null ? ' (saisie)' : ''}`
                    : undefined}
                /* L'AVIS DU PROF, sur une distance qu'on lui donne.
                 *
                 * Au départ, il affine ce que le carnet annonce ; ensuite il est
                 * la SEULE façon d'obtenir un avis, puisque l'application ne
                 * connaît plus la distance. */
                onOutil={() => setConseilOuvert(true)}
                outilOuvert={conseilOuvert}
                /* Le jeton monte dans l'en-tête : coincé entre le nom et le
                   chevron, il volait sa largeur au nom — sur un 360 px,
                   « Pitching wedge » se coupait dès que la suggestion
                   s'affichait. En en-tête il ne parle plus du club affiché mais
                   de l'étape : pour ce coup-ci, voici ce qu'on propose. */
                /* LE JETON D'EN-TÊTE, une fois la carte refermée.
                 *
                 * C'est lui qui répond à « pourquoi ce club est-il en main ? »
                 * quand le conseil vient de le mettre là et que la liste s'est
                 * fermée derrière. Sans lui, on retrouve un club changé sans
                 * savoir qui l'a changé.
                 *
                 * Il porte la distance quand elle a été donnée à la main : c'est
                 * la seule façon de vérifier qu'on a bien tapé 137 et non 13. */
                action={estPropose ? (
                    <button type="button" onClick={() => setChoixOuvert(true)}
                        aria-label="Pourquoi ce club ?"
                        className="relative flex shrink-0 items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-bold text-accent-content before:absolute before:-inset-2 before:content-['']">
                        {cibleSaisie != null ? (
                            <>
                                <GraduationCap size={11} aria-hidden="true" />
                                conseillé · {cibleSaisie} m
                            </>
                        ) : (
                            <>
                                <Lightbulb size={11} aria-hidden="true" /> suggéré
                            </>
                        )}
                    </button>
                ) : undefined}
                note={(
                    <>
                        {suggestion && !suggestion.sur && suggestion.resteM != null && clubChoisi === null && (
                            <p className="mt-1 text-center text-xs tabular-nums opacity-70">
                                {suggestion.resteM} m de plus que le {suggestion.club.label}
                            </p>
                        )}
                    </>
                )}
                /* La raison se lit À CÔTÉ des tuiles qu'elle départage — une
                   fenêtre modale recouvrait précisément la grille qu'on
                   comparait. */
                sousLaGrille={estPropose && suggestion ? (
                    <Pourquoi suggestion={suggestion} cibleM={cibleM} />
                ) : undefined}
            />

            {/* ③ LE RÉSULTAT — ce que la balle a fait.

                La dernière étape, et la seule qu'on touche à chaque balle : elle
                prend toute la hauteur qui reste sous les deux autres. Une fois
                la balle au fond, la même carte porte le score du trou et la
                sortie vers le suivant — c'est encore le résultat, celui du trou
                entier cette fois.

                Ce qu'on touche, et qui prend tout ce qui reste.

                Sur le GREEN, le pave d'issues n'a plus de sens : une balle
                puttée ne slice pas, ne gratte pas et ne socket pas. Il ne reste
                qu'une question, celle qu'on se pose vraiment en se relevant —
                elle est rentree, ou pas. Deux boutons pleine largeur valent
                mieux que sept dont cinq sont impossibles. */}
            {/* Le titre dit ce qu'on attend, et cela change trois fois.

                « Lie estimé » sur un coup plein : le mot du golf pour la façon
                dont la balle repose, et « estimé » parce que c'est bien une
                estimation prise de loin — le bilan qui suit permet de la
                corriger une fois arrivé près d'elle. Un titre qui promettrait
                un fait ferait hésiter avant chaque appui.

                Sur le green il n'y a plus de lie à estimer, et une fois la
                balle au fond il n'y a plus rien à saisir du tout. */}
            {/* L'étape épouse son contenu. Elle s'étirait pour porter six zones
                sous le pouce ; il n'en reste que deux boutons, et l'étirement
                laissait quatre cents pixels de blanc sous eux. */}
            <Etape n={3} className="flex flex-col"
                /* « Lie estimé » ne décrit plus rien : la carte ne demande plus
                   où la balle est tombée — cette question a son écran, après la
                   frappe. Ici on dit ce qui vient d'arriver. */
                titre={trouFini ? 'Résultat' : estPutt ? 'Le putt' : 'Le coup'}>
                {trouFini ? (
                    /* La balle est au fond. Le score du trou ne tient plus dans
                       ce bouton : il a son écran, monté par-dessus celui-ci —
                       voir `BilanTrou` plus bas. Ce qui reste ici est ce qu'on
                       retrouve si l'on ferme le plein écran sans changer de
                       trou : le score, et la sortie. */
                    <button type="button" disabled={trou >= etat.round.holeCount}
                        onClick={() => { setTrou((t) => t + 1); setClubChoisi(null); setChoixOuvert(false); }}
                        className="btn btn-accent min-h-0 flex-1 flex-col gap-2">
                        {hole?.par != null && (
                            <>
                                <IconeScore coups={coups} par={hole.par} size={38} />
                                {/* « Bogey », « Eagle » : des jambages ici aussi,
                                    et à quarante pixels la coupe se voit de
                                    loin. */}
                                <span className="font-score text-4xl leading-[1.15]">
                                    {ecartAuPar(coups, hole.par)}
                                </span>
                            </>
                        )}
                        <span className="flex items-center gap-1 text-base font-semibold opacity-80">
                            {trou >= etat.round.holeCount ? 'Dernier trou' : `Trou ${trou + 1}`}
                            {trou < etat.round.holeCount && <ChevronRight size={18} aria-hidden="true" />}
                        </span>
                    </button>
                ) : estPutt ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-3">
                        {/* Le drapeau en laiton, comme le check du pavé : sur
                            le green comme ailleurs, le bon résultat se
                            reconnaît à cette couleur avant d'être lu. */}
                        <button type="button" disabled={busy}
                            onClick={() => void enregistrerCoup({ landing: 'green', holed: true })}
                            className="btn btn-primary min-h-0 flex-[2] flex-col gap-1 text-2xl font-bold shadow-md">
                            <Flag aria-hidden="true" strokeWidth={2.5}
                                className="h-[clamp(2.5rem,14vw,4.5rem)] w-auto text-accent
                                           drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]" />
                            Rentré
                        </button>
                        {/* « À côté » était un `btn-outline` : bordure et texte
                            en `currentColor`, fond transparent. Ailleurs dans
                            l'application ce bouton se pose sur du papier
                            blanc et se lit très bien — mais ici le pavé est
                            à même le fairway, et un texte d'encre sombre sur
                            un vert profond ne se voyait plus du tout.

                            Il prend donc la brique du pavé de coups, avec son
                            bord : même aplat, même encre, mêmes contrastes
                            mesurés — 6,79:1 pour le texte, et un trait qui
                            détache le bouton de ce qu'il y a derrière. Le
                            joueur retrouve au putting la paire de couleurs
                            qu'il vient de quitter sur le fairway. */}
                        <button type="button" disabled={busy}
                            onClick={() => void enregistrerCoup({ landing: 'green' })}
                            className="flex min-h-0 w-full flex-1 items-center justify-center rounded-field
                                       text-xl font-bold shadow-md transition-transform
                                       active:scale-[0.98] disabled:opacity-40"
                            style={{
                                background: 'var(--brique)',
                                color: 'var(--brique-encre)',
                                border: '2px solid var(--brique-bord)',
                            }}>
                            À côté
                        </button>
                    </div>
                ) : (
                    <>
                        {/* DEUX boutons, et non six zones.
                            Le pavé demandait où la balle était tombée au moment
                            même où elle était encore en l'air : on répondait de
                            loin, et l'écran suivant servait à se corriger. Une
                            estimation qu'on sait fausse en la donnant n'aide
                            personne.

                            Ici, on dit seulement qu'on a frappé. Où la balle a
                            fini se dit à l'écran suivant, en marchant vers elle,
                            quand on le sait vraiment. */}
                        {/* De l'air autour des deux boutons.
                            Ils se touchaient presque, hérités d'une carte qui en
                            portait six et comptait ses pixels. Il n'en reste que
                            deux, et ce sont les seuls qu'on touche à chaque
                            balle : ils peuvent respirer. */}
                        <div className="flex flex-col gap-3 px-1 py-2">
                            <button type="button" disabled={busy || clubCourant == null}
                                onClick={() => void enregistrerCoup({ landing: null })}
                                className="btn btn-primary h-20 w-full gap-3 text-xl font-bold shadow-md">
                                {/* Le CHECK en laiton, et non le drapeau. Le
                                    drapeau désigne le trou — il sert déjà à ça
                                    sur le ruban et dans les puces de coup. Ici
                                    on ne désigne rien, on valide : c'est le
                                    même check laiton sur vert que le bon contact
                                    au practice. */}
                                <Check size={26} strokeWidth={3} aria-hidden="true"
                                    className="text-accent" />
                                Balle frappée
                            </button>

                            {/* Ce qui n'est PAS un coup joué : le sable touché, la
                                balle déplacée, le conseil demandé. La feuille
                                s'ouvre et se referme sur place — on n'a pas
                                frappé, donc il n'y a pas de coup à commenter
                                derrière. */}
                            <BoutonPenalite nombre={coutPenalitesTrou}
                                onOuvrir={() => setPenaliteOuverte(true)} />
                        </div>
                        {/* La sortie de secours du trou, et elle n'apparaît
                            qu'une fois le double bogey atteint.

                            Avant, elle n'a pas lieu d'être : on ne relève pas
                            une balle en étant au par. La montrer d'emblée
                            reviendrait à proposer d'abandonner à chaque départ,
                            ce qui est le contraire de ce qu'on veut dire. */}
                        {hole?.par != null && coups >= hole.par + 2 && (
                            <button type="button" disabled={busy} onClick={() => void relever()}
                                className="btn btn-ghost btn-sm shrink-0 gap-2 font-semibold"
                                style={{ color: 'var(--brique-encre)' }}>
                                <Hand size={15} aria-hidden="true" />
                                Relever la balle · {Math.max(coups, hole.par + 2)} coups
                            </button>
                        )}
                    </>
                )}

                {/* Une infraction se découvre parfois après coup, en recomptant
                    sa carte : le bouton reste donc atteignable une fois le trou
                    terminé, où les deux boutons du coup ont disparu. */}
                {trouFini && (
                    <div className="mt-2 shrink-0">
                        <BoutonPenalite nombre={coutPenalitesTrou}
                            onOuvrir={() => setPenaliteOuverte(true)} />
                    </div>
                )}
            </Etape>

            {/* Quitter, tout en bas.
                On sort d'un parcours comme on sort d'une pièce : par le fond,
                après avoir traversé. Le mot dit ce qu'il fait — « Terminer »
                laissait croire qu'on clôt la partie pour tout le monde, alors
                qu'on ne fait que ranger son téléphone ; la feuille qui suit
                pose la vraie question.

                Discret, en ligne de texte plutôt qu'en bouton plein : c'est le
                geste qu'on fait une fois, et il ne doit pas se disputer la
                place avec ceux qu'on fait à chaque balle. */}
            <button type="button" onClick={() => setConfirmSortie(true)}
                className="btn btn-ghost btn-sm mx-auto mt-1 gap-2 font-semibold !text-white/80
                           drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]">
                <LogOut size={15} aria-hidden="true" /> Quitter le parcours
            </button>

            {/* Le trou est fini : la fête, plein écran.

                Elle passe DEVANT le bilan du coup — le coup qui a fait tomber
                la balle n'a plus rien à faire commenter, et le joueur veut voir
                son score, pas régler une pénalité. */}
            {trouFini && !feteVue && (
                <BilanTrou
                    trou={trou}
                    nbTrous={nbTrous}
                    coups={coups}
                    par={hole?.par ?? null}
                    icone={hole?.par != null
                        ? <IconeScore coups={coups} par={hole.par} size={44} />
                        : <Flag size={44} aria-hidden="true" />}
                    /* Les clubs dans l'ordre où ils sont sortis du sac. Les
                       répétitions sont écrasées : « Putter → Putter → Putter »
                       raconte trois putts, ce que le nombre de coups dit déjà,
                       et noie les deux clubs qui ont fait le trou. */
                    clubs={coupsDuTrou
                        .map((sh) => clubs.find((c) => c.id === sh.clubId)?.label ?? null)
                        .filter((l): l is string => l != null)
                        .filter((l, i, tout) => tout[i - 1] !== l)}
                    releve={releve}
                    voisin={voisin}
                    ecartTour={parJoue > 0 ? total - parJoue : null}
                    /* Rouvrir le trou, c'est retirer le drapeau du coup qui
                       l'avait fermé — le coup lui-même reste, avec son club et
                       sa zone. On défait un appui de travers, pas un coup
                       joué ; celui-ci se défait à la corbeille du ruban, qui
                       est le geste prévu pour ça. */
                    onAnnuler={() => {
                        /* Deux façons de fermer un trou, deux façons de le
                           rouvrir. Une balle rentrée porte son drapeau sur un
                           coup ; une balle relevée le porte sur le trou, et le
                           score revient alors à ce qui a vraiment été joué —
                           les coups enregistrés plus leurs pénalités, sans le
                           plancher au double bogey. */
                        if (releve) {
                            void majCarte(coupsDuTrou.length + penalites, putts, false);
                            return;
                        }
                        const ferme = coupsDuTrou.find((sh) => sh.holed);
                        if (ferme != null) void retoucherCoup({ holed: false }, ferme.shotIndex);
                    }}
                    onSuivant={() => {
                        setFeteVue(true);
                        if (trou < nbTrous) {
                            setTrou((t) => t + 1);
                            setClubChoisi(null);
                            setChoixOuvert(false);
                            // L'écran de choix s'ouvrira de lui-même : la règle
                            // « trou neuf, pas encore de coup » s'en charge.
                            return;
                        }
                        /* Dernier trou fini : on rend la carte, et on la voit.
                           Le bouton passait par la feuille « quitter », qui
                           demande si l'on veut garder ou jeter le tour — une
                           question qui a du sens quand on s'en va au sixième
                           trou, aucune quand on vient de rentrer sa balle au
                           dix-huitième. Le joueur avait fini, et l'écran lui
                           demandait s'il était sûr. */
                        void terminer();
                    }}
                />
            )}

            {/* Le bilan du coup, plein écran.

                Il se monte APRÈS l'enregistrement, jamais avant : le coup est
                déjà dans la carte quand il s'ouvre, et le joueur qui range son
                téléphone sans rien toucher a son compte juste. C'est un bilan,
                pas un formulaire. */}
            {choixEcran && !trouFini && (
                <ChoixClubEcran
                    trou={trou}
                    nbTrous={etat.round.holeCount}
                    distance={cibleM != null ? `${cibleM} m` : null}
                    courantId={clubCourant?.id ?? null}
                    suggestionId={suggestion?.sur ? suggestion.club.id : null}
                    raison={suggestion?.sur && cibleM != null
                        ? `${cibleM} m à couvrir, et c'est le club qui s'en approche le plus.`
                        : null}
                    memoireId={clubMemoire?.id ?? null}
                    memoireLanding={passageCoup?.landing ?? null}
                    /* L'AVIS DU PROF. Au départ il affine la distance du carnet ;
                       ensuite il est la SEULE façon d'obtenir un conseil, puisque
                       l'application ne connaît plus ce qu'il reste à couvrir. */
                    onOutil={() => setConseilOuvert(true)}
                    outilOuvert={conseilOuvert}
                    clubs={sac.map((c) => ({
                        id: c.id,
                        label: c.label,
                        distance: c.referenceDistanceM != null ? `${c.referenceDistanceM} m` : null,
                        taux: c.rate != null ? Math.round(c.rate) : null,
                    }))}
                    onChoisir={(id) => {
                        setClubChoisi(id);
                        setConseilApplique(null);
                        setChoixEcran(false);
                    }}
                    onFermer={() => setChoixEcran(false)}
                />
            )}

            {/* LA SAISIE DE LA DISTANCE, par-dessus tout le reste.
                Montée ici et non dans l'un des deux sélecteurs : les deux
                l'ouvrent, et un modal qui vit à l'intérieur de la liste se
                retrouve à moitié sous le clavier numérique. */}
            {conseilOuvert && (
                <SaisieDistance
                    /* On part de ce qu'on sait déjà : la distance du trou au
                       départ, la dernière saisie ensuite, et à défaut la portée
                       du club en main — le champ est sélectionné, une valeur de
                       départ ne coûte donc rien à remplacer. */
                    initial={cibleSaisie
                        ?? (coups === 0 ? hole?.distanceM ?? null : null)
                        ?? clubCourant?.referenceDistanceM
                        ?? 100}
                    /* DEMANDER CONSEIL, C'EST LE SUIVRE.
                     *
                     * Le club conseillé est mis en main et la carte se referme :
                     * on ne fait pas relire une liste à quelqu'un qui vient
                     * d'accepter qu'on choisisse pour lui. La suggestion se
                     * recalcule ici, sur la distance qu'on vient de saisir —
                     * celle de l'état n'est pas encore à jour à cet instant.
                     *
                     * Rien n'est verrouillé pour autant : le sac se rouvre d'un
                     * geste, et le club se change comme avant. */
                    onValider={(m) => {
                        const conseil = clubPour(clubs, m);
                        setCibleSaisie(m);
                        if (conseil) {
                            setClubChoisi(conseil.club.id);
                            setConseilApplique(conseil.club.id);
                        }
                        setConseilOuvert(false);
                        setChoixOuvert(false);
                        setChoixEcran(false);
                    }}
                    onFermer={() => setConseilOuvert(false)}
                    onOublier={cibleSaisie != null
                        ? () => {
                            setCibleSaisie(null);
                            setConseilApplique(null);
                            setConseilOuvert(false);
                        }
                        : undefined}
                />
            )}

            {/* La zone part telle qu'elle est, `null` compris : c'est cet écran
                qui la demande désormais, et lui en souffler une — le fairway
                par défaut, comme avant — répondrait à la place du joueur. */}
            {bilan != null && (
                <BilanCoup
                    trou={trou}
                    nbTrous={etat.round.holeCount}
                    rang={bilan.shotIndex}
                    club={clubs.find((c) => c.id === bilan.clubId)?.label ?? null}
                    landing={bilan.landing ?? null}
                    penalty={bilan.penaltyReason}
                    total={coups}
                    par={hole?.par ?? null}
                    busy={busy}
                    onCorriger={(l) => void retoucherCoup({ landing: l })}
                    onPenalite={(p) => void retoucherCoup({ penaltyReason: p })}
                    onRentree={() => void retoucherCoup({ holed: true })}
                    /* Valider le lie mène au club suivant.
                       C'est l'enchaînement réel : la balle est posée quelque
                       part, donc il faut choisir avec quoi la reprendre. Au
                       putter on ne demande rien — un putt manqué est suivi d'un
                       autre putt, la question a déjà sa réponse. */
                    onSuivant={() => {
                        setCoupAuBilan(null);
                        if (!estPutt) setChoixEcran(true);
                    }}
                />
            )}

            {/* La liste des trous : le chemin de retour vers une saisie à
                corriger.
                Le ruban de score fait déjà voyager d'un tap, mais ses cases
                font trente pixels et ne portent qu'un chiffre : on y VÉRIFIE
                son tour, on n'y choisit pas un trou précis à rouvrir. Ici
                chaque trou tient une ligne entière, avec son par, sa distance
                et ce qu'on y a marqué — assez pour reconnaître le trou où l'on
                s'est trompé sans avoir à se souvenir de son numéro. */}
            {penaliteOuverte && (
                <FeuillePenalite
                    posees={penalitesTrou}
                    onAjouter={(r) => { void poserPenalite(r); }}
                    onRetirer={(i) => { void retirerPenalite(i); }}
                    onFermer={() => setPenaliteOuverte(false)} />
            )}

            {cartesOuvertes && (
                <CartesJoueurs roundId={etat.round.id} moi={moi}
                    onFermer={() => setCartesOuvertes(false)} />
            )}

            {trousOuvert && (
                <div className="modal modal-open" role="dialog"
                    onClick={(e) => { if (e.target === e.currentTarget) setTrousOuvert(false); }}>
                    <div className="modal-box max-w-md overflow-hidden p-0">
                        <header className="flex items-start gap-3 bg-[var(--fairway-deep)] px-4 py-3.5">
                            <div className="min-w-0 flex-1">
                                <h3 className="font-display text-lg font-bold leading-tight text-white">
                                    Les {etat.round.holeCount} trous
                                </h3>
                                <p className="mt-0.5 text-xs font-semibold text-white/60">
                                    Touchez un trou pour y revenir et corriger votre carte
                                </p>
                            </div>
                            {/* La bascule vit ICI, et non sous le ruban.
                                Le ruban tient sur huit cases dans un bandeau
                                déjà chargé ; cette fenêtre s'ouvre en le
                                touchant, c'est la carte complète du tour, et
                                c'est le seul endroit qu'on atteigne aussi en
                                jouant seul — la pile des partenaires, elle,
                                n'existe qu'à plusieurs. */}
                            {netLisible && (
                                <div className="shrink-0">
                                    <BasculeNet net={scoreNet} onChange={setScoreNet} />
                                </div>
                            )}
                        </header>

                        <ul className="max-h-[55vh] overflow-y-auto p-2">
                            {etat.holes.map((h) => {
                                const carteTrou = etat.myHoles.find((m) => m.holeNumber === h.number);
                                const joue = carteTrou?.strokes != null;
                                const rendu = mesRendus?.parTrou.get(h.number) ?? 0;
                                // Le même score que le ruban, sans quoi la même
                                // carte se lirait de deux façons à deux endroits.
                                const coupsTrou = carteTrou?.strokes == null ? null
                                    : carteTrou.strokes - (netLisible && scoreNet ? rendu : 0);
                                const ecart = coupsTrou != null && h.par != null
                                    ? coupsTrou - h.par : null;
                                const ici = h.number === trou;
                                return (
                                    <li key={h.number}>
                                        <button type="button"
                                            onClick={() => {
                                                setTrou(h.number);
                                                setClubChoisi(null);
                                                setChoixOuvert(false);
                                                setTrousOuvert(false);
                                            }}
                                            aria-current={ici ? 'true' : undefined}
                                            className={`flex w-full items-center gap-3 rounded-field px-2.5 py-2 text-left transition-colors ${
                                                ici ? 'bg-accent/20 ring-1 ring-accent' : 'hover:bg-base-200'
                                            }`}>
                                            <span className="w-5 shrink-0 text-center text-xs font-bold tabular-nums opacity-50">
                                                {h.number}
                                            </span>
                                            {/* Le même vocabulaire de formes que
                                                le ruban : cercle sous le par,
                                                carré au-dessus. Il se lit sans
                                                légende parce qu'on l'a déjà vu
                                                deux écrans plus haut. */}
                                            <span className={`flex size-9 shrink-0 items-center justify-center font-score text-base ${allure(ecart)}`}>
                                                {coupsTrou ?? ''}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-bold leading-tight">
                                                    {joue && h.par != null
                                                        ? ecartAuPar(coupsTrou!, h.par)
                                                        : joue ? `${coupsTrou} coups` : 'à jouer'}
                                                </span>
                                                <span className="block text-xs leading-tight opacity-60">
                                                    {[
                                                        h.par != null ? `par ${h.par}` : null,
                                                        h.distanceM != null ? `${h.distanceM} m` : null,
                                                        carteTrou?.putts != null && carteTrou.putts > 0
                                                            ? `${carteTrou.putts} putt${carteTrou.putts > 1 ? 's' : ''}` : null,
                                                        // Les coups rendus se disent en toutes
                                                        // lettres ici : la ligne a la place, et
                                                        // les points du ruban n'y sont pas.
                                                        rendu > 0 ? `${rendu} rendu${rendu > 1 ? 's' : ''}` : null,
                                                    ].filter(Boolean).join(' · ')}
                                                </span>
                                            </span>
                                            {ici
                                                ? <span className="shrink-0 text-[11px] font-bold text-[color:var(--brass-ink)]">ici</span>
                                                : <ChevronRight size={16} className="shrink-0 opacity-30" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="border-t border-base-300 p-3">
                            <button type="button" onClick={() => setTrousOuvert(false)}
                                className="btn btn-block h-12 font-bold">Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Terminer et annuler sous la même porte : ce sont les deux façons
                de quitter, et les séparer par leur libellé vaut mieux que deux
                boutons qui traînent sur l'écran de jeu. */}
            {confirmSortie && (
                <div className="modal modal-open" role="dialog">
                    <div className="modal-box">
                        <h3 className="text-lg font-bold">Quitter ce parcours ?</h3>
                        <p className="mt-2 text-sm opacity-80">
                            {trousJoues > 0
                                ? `${trousJoues} trou${trousJoues > 1 ? 's' : ''} enregistré${trousJoues > 1 ? 's' : ''}.`
                                : "Rien n'a encore été enregistré."}
                            {partenaires > 0 && ' La partie continue pour les autres.'}
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy} onClick={() => void terminer()}
                                className="btn btn-primary btn-block h-14 font-bold tap-target">
                                Terminer et garder la carte
                            </button>
                            {/* « Quitter » et non « annuler la partie » : ce
                                geste n'efface QUE sa propre carte. Les autres
                                continuent, et le tour ne disparaît que s'il ne
                                reste personne. */}
                            <button type="button" disabled={busy} onClick={() => void annuler()}
                                className="btn btn-outline btn-error btn-block h-14 gap-2 tap-target">
                                <Trash2 size={16} />
                                {partenaires > 0 ? 'Quitter sans garder ma carte' : 'Annuler, ne rien garder'}
                            </button>
                            <button type="button" onClick={() => setConfirmSortie(false)}
                                className="btn btn-ghost btn-block tap-target">
                                Continuer à jouer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
